import React, { createContext, useContext, useState, useEffect } from 'react';

// ============================================================
// SUPABASE SETUP
// Replace these with your actual Supabase project credentials
// from: https://app.supabase.com → Project Settings → API
// ============================================================
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://xaqayispsgbahmtiphmq.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'sb_publishable_wjMIi8riidtIKdJWQIdRJQ_j79r8855';

// We lazy-import Supabase so the app still works in demo mode without credentials
let supabase = null;
try {
  if (!SUPABASE_URL.includes('YOUR_SUPABASE_URL')) {
    const { createClient } = require('@supabase/supabase-js');
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  }
} catch (e) {
  console.log('Supabase not configured - running in demo mode');
}

const AuthContext = createContext(null);

// Demo user for testing before Supabase is connected
const DEMO_USER = {
  id: 'demo-001',
  email: 'provider@neurogenx.com',
  user_metadata: {
    full_name: 'Dr. Sarah Mitchell',
    practice_name: 'Mitchell Pain & Wellness Center',
    phone: '(555) 234-5678',
    address: '1200 Medical Blvd, Suite 300',
    city: 'Dallas',
    state: 'TX',
    zip: '75201',
    npi: '1234567890',
    subscription_tier: 'Preferred Provider',
    stripe_customer_id: '',
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (supabase) {
      // Real Supabase auth
      supabase.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null);
        if (session?.user) fetchProfile(session.user.id);
        setLoading(false);
      });

      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) fetchProfile(session.user.id);
      });

      return () => subscription.unsubscribe();
    } else {
      // Demo mode - check localStorage
      const savedUser = localStorage.getItem('ngx_demo_user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
        setProfile(JSON.parse(savedUser).user_metadata);
      }
      setLoading(false);
    }
  }, []);

  const fetchProfile = async (userId) => {
    if (!supabase) return;
    const { data } = await supabase
      .from('provider_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) setProfile(data);
  };

  const signIn = async (email, password) => {
    if (supabase) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data;
    } else {
      // Demo mode login
      if (email === 'demo@neurogenx.com' && password === 'demo123') {
        const user = { ...DEMO_USER, email };
        localStorage.setItem('ngx_demo_user', JSON.stringify(user));
        setUser(user);
        setProfile(user.user_metadata);
        return user;
      } else {
        throw new Error('Invalid credentials. Use demo@neurogenx.com / demo123');
      }
    }
  };

  const signOut = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    } else {
      localStorage.removeItem('ngx_demo_user');
      setUser(null);
      setProfile(null);
    }
  };

  const updateProfile = async (updates) => {
    if (supabase) {
      const { error } = await supabase.auth.updateUser({ data: updates });
      if (error) throw error;
      const { error: profileError } = await supabase
        .from('provider_profiles')
        .upsert({ id: user.id, ...updates });
      if (profileError) throw profileError;
    } else {
      // Demo mode
      const updated = { ...user, user_metadata: { ...user.user_metadata, ...updates } };
      localStorage.setItem('ngx_demo_user', JSON.stringify(updated));
      setUser(updated);
      setProfile(updated.user_metadata);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut, updateProfile, supabase }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
